import { TQuote } from '../types';
import { eventLog, TTickEvent } from './EventLog';

export type TEventStreamFilter = {
    symbol?: string;
    granularity?: number;
    minEpoch?: number;
    maxEpoch?: number;
    source?: 'live' | 'history' | 'both';
};

export type TEventIteratorOptions = {
    startEpoch?: number;
    endEpoch?: number;
    reverse?: boolean;
};

class EventStream {
    slice(filter?: TEventStreamFilter): TTickEvent[] {
        const minEpoch = filter?.minEpoch ?? -Infinity;
        const maxEpoch = filter?.maxEpoch ?? Infinity;
        const rangeEvents = eventLog.getRange(minEpoch, maxEpoch);
        if (!filter) return rangeEvents;
        return rangeEvents.filter(e => {
            if (filter.symbol && e.symbol !== filter.symbol) return false;
            if (filter.granularity !== undefined && e.granularity !== filter.granularity) return false;
            if (filter.source && filter.source !== 'both' && e.source !== filter.source) return false;
            return true;
        });
    }
    getAll() { return this.slice(); }
    getTimeWindow(startEpoch: number, endEpoch: number) { return eventLog.getRange(startEpoch, endEpoch); }
    getLatest(count: number) { const all = this.slice(); return all.slice(-count); }
    findClosest(targetEpoch: number): TTickEvent | undefined {
        const all = this.slice(); if (!all.length) return undefined;
        let left = 0, right = all.length - 1, closest = all[0], minDist = Math.abs(all[0].epoch - targetEpoch);
        while (left <= right) {
            const mid = (left + right) >> 1; const dist = Math.abs(all[mid].epoch - targetEpoch);
            if (dist < minDist) { minDist = dist; closest = all[mid]; }
            if (all[mid].epoch < targetEpoch) left = mid + 1; else if (all[mid].epoch > targetEpoch) right = mid - 1; else return all[mid];
        }
        return closest;
    }
    *iterator(opts?: TEventIteratorOptions): Generator<TTickEvent> {
        const events = this.slice({ minEpoch: opts?.startEpoch, maxEpoch: opts?.endEpoch });
        if (opts?.reverse) { for (let i = events.length - 1; i >= 0; i--) yield events[i]; } else { for (const e of events) yield e; }
    }
    getStats(filter?: TEventStreamFilter) {
        const events = this.slice(filter);
        if (!events.length) return { count: 0, firstEpoch: null, lastEpoch: null, timeSpan: 0, sources: { live: 0, history: 0 } };
        const sources = { live: 0, history: 0 } as { live: number; history: number };
        events.forEach(e => sources[e.source]++);
        return { count: events.length, firstEpoch: events[0].epoch, lastEpoch: events[events.length - 1].epoch, timeSpan: events[events.length - 1].epoch - events[0].epoch, sources };
    }
    hasEventsInRange(startEpoch: number, endEpoch: number) { return !!this.getTimeWindow(startEpoch, endEpoch).length; }
    getBounds() { const latest = eventLog.latest(); const all = this.slice(); return { first: all.length ? all[0].epoch : null, last: latest?.epoch ?? null }; }
    sample(targetCount: number, filter?: TEventStreamFilter): TTickEvent[] {
        const events = this.slice(filter); if (events.length <= targetCount) return events;
        const step = Math.floor(events.length / targetCount); const sampled: TTickEvent[] = [];
        for (let i = 0; i < events.length; i += step) sampled.push(events[i]);
        if (sampled[sampled.length - 1] !== events[events.length - 1]) sampled.push(events[events.length - 1]);
        return sampled;
    }
    toQuotes(events: TTickEvent[]): TQuote[] { return events.map(e => e.raw); }
}
export const eventStream = new EventStream();
